import React, { useState, useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Globe, Menu, UserRound, X } from "lucide-react";
import { Link } from "react-router-dom";
import { getExternalWebsiteUrl } from "../config/externalConfig";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const languageRef = useRef(null);

  // Close language dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languageRef.current && !languageRef.current.contains(event.target)) {
        setIsLanguageOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle scroll to show/hide first row
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const navigationItems = [
    { name: "HOTELS & RESORTS", href: "/hotels" },
    { name: "RITZ-CARLTON RESERVE", href: "/reserve" },
    { name: "RESIDENCES", href: "/residences" },
    { name: "YACHTS", href: "/yachts" },
    { name: "ABOUT THE RITZ-CARLTON", href: "/about" },
    { name: "THE JOURNEY", href: "/journey" },
  ];

  const languages = [
    { code: "en", name: "English" },
    { code: "es", name: "Español" },
    { code: "fr", name: "Français" },
    { code: "de", name: "Deutsch" },
    { code: "it", name: "Italiano" },
    { code: "pt", name: "Português" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-[0_4px_10px_0_rgba(0,0,0,0.1)] transition-all duration-300">
      <div className="max-w-[1140px] mx-auto px-4">
        {/* First Row - Logo, Language, Sign In */}
        <div className={`grid grid-cols-[40px_minmax(0,1fr)_88px] items-center md:flex md:justify-between h-14 transition-all duration-300 overflow-hidden ${
          isScrolled ? 'h-0 opacity-0' : 'h-14 opacity-100'
        }`}>
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-1 justify-self-start"
          >
            {isMenuOpen ? (
              <X className="w-4 h-4 text-[#646464]" />
            ) : (
              <Menu className="w-4 h-4 text-[#646464]" />
            )}
          </button>

          {/* Logo */}
          <Link to="/" className="flex items-center justify-self-center md:justify-self-auto">
            <img 
              src="https://customer-assets.emergentagent.com/job_ritzcarltonnext/artifacts/v9fuw50h_the-ritz-carlton.logo.png"
              alt="The Ritz-Carlton"
              className="h-[41px] w-[208.16px] max-w-full object-contain"
            />
          </Link>

          {/* Language Toggle & Sign In */}
          <div className="flex items-center gap-1 md:gap-4 justify-self-end">
            <div className="relative" ref={languageRef}>
              <button
                type="button"
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className="md:hidden inline-flex h-9 w-9 items-center justify-center text-[#1c1c1c]"
                aria-label="Select language"
              >
                <Globe className="h-3 w-3" strokeWidth={2.5} />
              </button>

              <div className="hidden md:block">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                  className="flex items-center space-x-1 bg-transparent hover:bg-transparent text-[#1c1c1c] hover:text-[#1c1c1c] font-normal border-none p-2"
                  style={{ 
                    fontFamily: 'Swiss-721, Arial, sans-serif',
                    fontSize: '0.8125rem',
                    fontWeight: 400,
                    WebkitFontSmoothing: 'antialiased',
                    textRendering: 'optimizeLegibility'
                  }}
                >
                  <Globe className="w-3 h-3" />
                  <span>English</span>
                </Button>
              </div>

              {/* Language Dropdown */}
              {isLanguageOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-50 min-w-[120px]">
                  {languages.map((language, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setIsLanguageOpen(false);
                        console.log(`Language changed to: ${language.name}`);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 first:rounded-t-md last:rounded-b-md"
                      style={{ 
                        fontFamily: 'Swiss-721, Arial, sans-serif',
                        fontSize: '0.75rem'
                      }}
                    >
                      {language.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href={`${getExternalWebsiteUrl()}/login`}
              className="md:hidden inline-flex h-9 w-9 items-center justify-center text-[#1c1c1c]"
              aria-label="Sign in or join"
            >
              <UserRound className="h-3.5 w-3.5" strokeWidth={2.5} />
            </a>
            
            <div className="hidden md:block">
              <Button
                className="rounded-none transition-all duration-[1200ms] antialiased"
                style={{ 
                  fontFamily: 'var(--t-base-font-family)',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  WebkitFontSmoothing: 'antialiased',
                  textRendering: 'optimizeLegibility',
                  width: '170px',
                  height: '50px',
                  minWidth: '170px',
                  minHeight: '50px',
                  maxWidth: '170px',
                  maxHeight: '50px',
                  background: '#ffffff',
                  color: '#1c1c1c',
                  border: '1px solid #1c1c1c',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  lineHeight: '1rem',
                  padding: '0',
                  borderRadius: '0px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  outline: 'none'
                }}
                asChild
              >
                <a href={`${getExternalWebsiteUrl()}/login`} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', textDecoration: 'none', color: 'inherit' }}>Sign In or Join</a>
              </Button>
            </div>

          </div>
        </div>

        {/* Second Row - Navigation & Reserve Now */}
        <div className="hidden md:flex items-center justify-between h-14">
          {/* Navigation Menu */}
          <nav className="flex items-center space-x-8">
            {navigationItems.map((item, index) => (
              <Link
                key={index}
                to={item.href}
                className="text-xs leading-4 font-normal text-[rgb(28,28,28)] hover:text-[rgb(28,28,28)] uppercase tracking-[0.025em] transition-[color,background-color,border-color,text-decoration-color,fill,stroke] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] antialiased relative group pb-6"
                style={{ 
                  fontFamily: 'Swiss-721, Arial, sans-serif',
                  fontSize: '12px',
                  WebkitFontSmoothing: 'antialiased'
                }}
              >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-1 bg-black transition-all duration-200 group-hover:w-full"></span>
              </Link>
            ))}
          </nav>

          {/* Reserve Now Button */}
          <Button
            className="rounded-none transition-all duration-[1200ms] antialiased"
            style={{ 
              fontFamily: 'var(--t-base-font-family)',
              fontSize: '0.8125rem',
              fontWeight: 700,
              WebkitFontSmoothing: 'antialiased',
              textRendering: 'optimizeLegibility',
              width: '170px',
              height: '50px',
              minWidth: '170px',
              minHeight: '50px',
              maxWidth: '170px',
              maxHeight: '50px',
              background: '#1c1c1c',
              color: '#ffffff',
              border: '1px solid #1c1c1c',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              lineHeight: '1rem',
              padding: '0',
              borderRadius: '0px',
              cursor: 'pointer',
              textDecoration: 'none',
              outline: 'none'
            }}
          >
            Reserve Now
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-[#f4f4f4]">
            <nav className="space-y-3">
              {navigationItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.href}
                  className="block text-xs leading-4 font-normal text-[rgb(28,28,28)] hover:text-[rgb(28,28,28)] uppercase tracking-[0.025em] transition-[color,background-color,border-color,text-decoration-color,fill,stroke] duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] py-1 antialiased relative group pb-4"
                  style={{ 
                    fontFamily: 'Swiss-721, Arial, sans-serif',
                    fontSize: '12px',
                    WebkitFontSmoothing: 'antialiased'
                  }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                  <span className="absolute bottom-1 left-0 w-0 h-1 bg-black transition-all duration-200 group-hover:w-full"></span>
                </Link>
              ))}
              <div className="pt-3 border-t border-[#f4f4f4] space-y-2">
                <Button
                  className="w-full bg-[#1c1c1c] text-white hover:bg-[#646464] hover:text-white font-bold rounded-none h-[50px] px-4 transition-all duration-[1200ms] antialiased"
                  style={{ 
                    fontFamily: 'Swiss-721, Arial, sans-serif',
                    fontSize: '0.8125rem',
                    fontWeight: 700,
                    WebkitFontSmoothing: 'antialiased',
                    textRendering: 'optimizeLegibility'
                  }}
                >
                  Reserve Now
                </Button>
                <div className="relative">
                  <Button
                    variant="ghost"
                    onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                    className="w-full justify-start bg-transparent hover:bg-transparent text-[#1c1c1c] hover:text-[#1c1c1c] font-normal border-none p-2"
                    style={{ 
                      fontFamily: 'Swiss-721, Arial, sans-serif',
                      fontSize: '0.8125rem',
                      fontWeight: 400,
                      WebkitFontSmoothing: 'antialiased',
                      textRendering: 'optimizeLegibility'
                    }}
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    English
                  </Button>

                  {/* Mobile Language Dropdown */}
                  {isLanguageOpen && (
                    <div className="mt-1 bg-gray-50 border border-gray-200 rounded-md">
                      {languages.map((language, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            setIsLanguageOpen(false);
                            console.log(`Language changed to: ${language.name}`);
                          }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 first:rounded-t-md last:rounded-b-md"
                          style={{ 
                            fontFamily: 'Swiss-721, Arial, sans-serif',
                            fontSize: '0.75rem'
                          }}
                        >
                          {language.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
